import { useMemo, useState } from "react";
import Select from "react-select";
import { getCustomSelectStyles } from "../../../../shared";
import type { ThemeClasses } from "../types";
import type {
  TransferBackup,
  TransferStatusPolicy,
} from "../../../../../../features/devices/mutations";

const ALL_SOURCES = ["positions", "events", "commands", "geofences"] as const;

const STATUS_POLICIES = ["keep", "active", "inactive"] as const;

interface CompanyOption {
  id: string;
  name: string;
  origin?: string[] | null;
}

interface DeviceBulkTransferModalProps {
  count: number;
  companies: CompanyOption[];
  currentCompanyId: string | null;
  isDarkMode: boolean;
  themeClasses: ThemeClasses;
  onClose: () => void;
  onConfirm: (
    toCompanyId: string,
    backup: TransferBackup,
    statusPolicy: TransferStatusPolicy,
  ) => void;
  isPending?: boolean;
  t: (key: string, options?: any) => string;
}

export function DeviceBulkTransferModal({
  count,
  companies,
  currentCompanyId,
  isDarkMode,
  themeClasses,
  onClose,
  onConfirm,
  isPending = false,
  t,
}: DeviceBulkTransferModalProps) {
  const [targetId, setTargetId] = useState<string | null>(null);
  const [statusPolicy, setStatusPolicy] = useState<TransferStatusPolicy>("keep");
  const [pendingBackup, setPendingBackup] = useState<TransferBackup | null>(null);
  const [sel, setSel] = useState<Record<string, boolean>>({
    positions: true,
    events: true,
    commands: true,
    geofences: true,
  });

  const options = useMemo(
    () =>
      companies
        .filter((c) => c.id !== currentCompanyId)
        .map((c) => ({ value: c.id, label: c.name })),
    [companies, currentCompanyId],
  );

  const target = companies.find((c) => c.id === targetId) || null;
  const selectedSources = ALL_SOURCES.filter((s) => sel[s]);
  const muted = isDarkMode ? "text-gray-300" : "text-gray-500";
  const warnBox = isDarkMode
    ? "border-amber-500/30 bg-amber-500/10 text-amber-200"
    : "border-amber-300 bg-amber-50 text-amber-800";
  const dangerBox = isDarkMode
    ? "border-red-500/30 bg-red-500/10 text-red-200"
    : "border-red-300 bg-red-50 text-red-800";

  const confirming = pendingBackup !== null;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4"
      onClick={isPending ? undefined : onClose}
    >
      <div
        className={`max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl border p-6 shadow-xl ${themeClasses.cardBg} ${themeClasses.cardBorder}`}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className={`text-lg font-bold ${themeClasses.text}`}>
          {t("devices.transfer.bulk.title")}
        </h2>
        <p className={`mt-1 text-sm ${muted}`}>
          {t("devices.transfer.bulk.subtitle", { count })}
        </p>

        {confirming ? (
          <div className="mt-4 space-y-3">
            <div className={`rounded-lg border px-3 py-2 text-xs ${dangerBox}`}>
              {t("devices.transfer.bulk.confirmWarning", {
                count,
                company: target?.name,
              })}
            </div>
            <div className={`rounded-lg border px-3 py-2 text-xs ${warnBox}`}>
              {t("devices.transfer.bulk.sgNotice")}
            </div>
            <div className="flex flex-wrap justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setPendingBackup(null)}
                disabled={isPending}
                className={`rounded-lg px-3 py-2 text-sm font-medium ${
                  isDarkMode
                    ? "text-gray-300 hover:bg-white/5"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                {t("devices.transfer.confirm.back")}
              </button>
              <button
                type="button"
                disabled={isPending}
                onClick={() =>
                  targetId && onConfirm(targetId, pendingBackup!, statusPolicy)
                }
                className="rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {isPending
                  ? t("devices.transfer.processing")
                  : t("devices.transfer.confirm.accept")}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="mt-4">
              <label
                className={`block text-sm font-medium mb-1 ${themeClasses.text}`}
              >
                {t("devices.transfer.targetLabel")}
              </label>
              <Select
                options={options}
                styles={getCustomSelectStyles(isDarkMode, false)}
                placeholder={t("devices.transfer.targetPlaceholder")}
                value={options.find((o) => o.value === targetId) || null}
                onChange={(opt: any) => setTargetId(opt ? opt.value : null)}
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            <div className="mt-4">
              <p className={`text-sm font-medium ${themeClasses.text}`}>
                {t("devices.transfer.status.label")}
              </p>
              <p className={`text-xs mb-2 ${muted}`}>
                {t("devices.transfer.bulk.statusHint")}
              </p>
              <div className="space-y-1">
                {STATUS_POLICIES.map((p) => (
                  <label
                    key={p}
                    className={`flex cursor-pointer items-center gap-3 rounded-lg px-2 py-1.5 ${
                      isDarkMode ? "hover:bg-white/5" : "hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="radio"
                      name="bulk-transfer-status-policy"
                      className="h-4 w-4 accent-primary-500"
                      checked={statusPolicy === p}
                      onChange={() => setStatusPolicy(p)}
                    />
                    <span className={`text-sm ${themeClasses.text}`}>
                      {t(`devices.transfer.status.options.${p}`)}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="mt-4">
              <p className={`text-sm font-medium ${themeClasses.text}`}>
                {t("devices.transfer.backupLabel")}
              </p>
              <p className={`text-xs mb-2 ${muted}`}>
                {t("devices.transfer.backupHint")}
              </p>
              <div className="space-y-1">
                {ALL_SOURCES.map((s) => (
                  <label
                    key={s}
                    className={`flex cursor-pointer items-center gap-3 rounded-lg px-2 py-1.5 ${
                      isDarkMode ? "hover:bg-white/5" : "hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-primary-500"
                      checked={sel[s]}
                      onChange={(e) =>
                        setSel((p) => ({ ...p, [s]: e.target.checked }))
                      }
                    />
                    <span className={`text-sm ${themeClasses.text}`}>
                      {t(`devices.transfer.sources.${s}`)}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="mt-6 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className={`rounded-lg px-3 py-2 text-sm font-medium ${
                  isDarkMode
                    ? "text-gray-300 hover:bg-white/5"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                {t("devices.transfer.cancel")}
              </button>
              <button
                type="button"
                disabled={!targetId}
                onClick={() => setPendingBackup({ mode: "purge" })}
                className={`rounded-lg px-3 py-2 text-sm font-medium border ${
                  isDarkMode
                    ? "border-dark-border hover:bg-white/5 text-gray-200"
                    : "border-gray-200 hover:bg-gray-50 text-gray-700"
                } disabled:opacity-40 disabled:cursor-not-allowed`}
              >
                {t("devices.transfer.confirmNoBackup")}
              </button>
              <button
                type="button"
                disabled={!targetId || selectedSources.length === 0}
                onClick={() =>
                  setPendingBackup({
                    mode: "archive",
                    sources: selectedSources,
                  })
                }
                className="rounded-lg bg-primary-500 px-3 py-2 text-sm font-medium text-white hover:bg-primary-400 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {t("devices.transfer.confirmBackup")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
